import { call, put, takeLatest } from "redux-saga/effects";
import request, { generateSignature, hashfameRequest } from "utils/request";
import { marketplaceActions } from "./index";
import { showToast } from "components/ToastContainer/index";

function* fetchMarketplaceList(action) {
  try {
    const {
      page = 1,
      limit = 10,
      status = "",
      service = "",
      search = "",
      sort_by,
      sort_order = "ASC",
      period = "",
      from_date = null,
      to_date = null
    } = action.payload || {};
    const queryParams = new URLSearchParams({ page, limit });
    if (status) queryParams.append("status", status);
    if (search) queryParams.append("search", search);
    if (service) queryParams.append("service", service);
    if (sort_by) queryParams.append("sort_by", sort_by);
    if (sort_order) queryParams.append("sort_order", sort_order);
    if (period) queryParams.append("period", period);
    if (from_date) queryParams.append("from_date", from_date);
    if (to_date) queryParams.append("to_date", to_date);

    const data = yield call(
      request,
      `/api/marketplace?${queryParams.toString()}`,
      { method: "GET", credentials: "include" }
    );

    if (data?.success) {
      yield put(marketplaceActions.fetchMarketplaceSuccess(data));
    }
  } catch (error) {
    yield put(marketplaceActions.fetchMarketplaceFailure(error.message));
  }
}

function* getMarketplaceByIdSaga(action) {
  try {
    const { id } = action.payload;

    const data = yield call(
      request,
      `/api/marketplace/getMarketplace?id=${id}`,
      {
        method: "GET",
        credentials: "include"
      }
    );

    if (data?.success) {
      yield put(marketplaceActions.getMarketplaceByIdSuccess(data.data));
    }
  } catch (error) {
    yield put(marketplaceActions.getMarketplaceByIdFailure(error.message));
    showToast(error.message || "Failed to load campaign", "danger");
  }
}

function* updateMarketplaceById(action) {
  try {
    const { id, payload, navigate, location } = action.payload;
    const data = yield call(request, `/api/marketplace/${id}`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
      credentials: "include"
    });

    if (data?.success) {
      yield put(marketplaceActions.updateMarketplaceSuccess(data.data));
      if (navigate) {
        navigate(`/marketplace${location.search}`);
        showToast(data?.message, "success");
      }
    }
  } catch (error) {
    yield put(marketplaceActions.updateMarketplaceFailure(error.message));
    showToast(error?.message || "Update failed", "danger");
  }
}

// create - campaign
function* fetchMarketplaceUserSaga(action) {
  try {
    const { user_phone_number } = action.payload;

    if (!user_phone_number) {
      throw new Error("Phone number is required");
    }

    const response = yield call(
      request,
      `/api/marketplace/user?user_phone_number=${user_phone_number}`,
      { method: "GET" }
    );

    // IMPORTANT CHECK
    if (!response.success) {
      throw new Error(response.message);
    }

    yield put(marketplaceActions.fetchMarketplaceUserSuccess(response.data));
  } catch (error) {
    const errorMessage = error?.message || "Failed to fetch marketplace user";

    yield put(marketplaceActions.fetchMarketplaceUserError(errorMessage));

    showToast(errorMessage, "danger");
  }
}

function* createMarketplaceCampaignSaga(action) {
  try {
    const { data, navigate, onSuccess } = action.payload || {};

    if (!data || typeof data !== "object") {
      throw new Error("Invalid campaign payload");
    }

    const formData = new FormData();

    // THIS IS THE KEY FIX
    formData.append("data", JSON.stringify(data));

    const response = yield call(request, "/api/marketplace/campaign/create", {
      method: "POST",
      body: formData,
      credentials: "include"
    });

    if (response?.success) {
      yield put(
        marketplaceActions.createMarketplaceCampaignSuccess(response.data)
      );
      showToast(response.message || "Campaign created", "success");
      if (typeof onSuccess === "function") {
        onSuccess();
      }

      navigate("/marketplace");
    } else {
      throw new Error(response?.message || "Campaign creation failed");
    }
  } catch (error) {
    yield put(
      marketplaceActions.createMarketplaceCampaignFailure(error.message)
    );
    showToast(error.message || "Campaign creation failed", "danger");
  }
}

function* fetchCampaignReportsSaga(action) {
  try {
    const {
      search = "",
      reason = "",
      report_status = "",
      page = 1,
      limit = 10,
      sort_by = null,
      sort_order = "desc"
    } = action.payload || {};

    const queryParams = new URLSearchParams({ page, limit });
    if (search) queryParams.append("search", search);
    if (reason) queryParams.append("reason", reason);
    if (report_status) queryParams.append("report_status", report_status);
    if (sort_by) queryParams.append("sort_by", sort_by);
    if (sort_order) queryParams.append("sort_order", sort_order);

    const response = yield call(
      request,
      `/api/campaign-reports?${queryParams.toString()}`,
      { method: "GET", credentials: "include" }
    );

    if (response?.success) {
      yield put(marketplaceActions.fetchCampaignReportsSuccess(response));
    } else {
      throw new Error(response?.message || "Failed to fetch campaign reports");
    }
  } catch (error) {
    yield put(marketplaceActions.fetchCampaignReportsFailure(error.message));
    showToast(error.message || "Failed to fetch campaign reports", "danger");
  }
}

function* fetchPosterCountsSaga(action) {
  try {
    const { userId } = action.payload || {};

    const queryParams = new URLSearchParams();
    if (userId) queryParams.append("userId", userId);

    const response = yield call(
      request,
      `/api/campaign-reports/poster-counts?${queryParams.toString()}`,
      { method: "GET", credentials: "include" }
    );

    if (response?.success) {
      yield put(marketplaceActions.fetchPosterCountsSuccess(response));
    } else {
      throw new Error(response?.message || "Failed to fetch poster counts");
    }
  } catch (error) {
    yield put(marketplaceActions.fetchPosterCountsFailure(error.message));
    showToast(error.message || "Failed to fetch poster counts", "danger");
  }
}

function* updateCampaignReportStatusSaga(action) {
  try {
    const { reportId, report_status, is_hidden } = action.payload;

    const response = yield call(
      request,
      `/api/campaign-reports/${reportId}/status`,
      {
        method: "POST", // must match router.patch
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ report_status, is_hidden })
      }
    );

    if (response?.success) {
      yield put(
        marketplaceActions.updateCampaignReportStatusSuccess({
          ...response.data,
          reportId: Number(action.payload.reportId)
        })
      );
      yield put(
        marketplaceActions.fetchCampaignReports({
          page: 1,
          limit: 10,
          sort_order: "desc"
        })
      );
      showToast("Status updated successfully", "success");
    } else {
      throw new Error(response?.message || "Failed to update status");
    }
  } catch (error) {
    yield put(
      marketplaceActions.updateCampaignReportStatusFailure(error.message)
    );
    showToast(error.message || "Failed to update status", "danger");
  }
}

function* updateMarketplaceRequestStatusSaga(action) {
  try {
    const {
      marketplace_id_hash,
      status,
      collaboration_type,
      reject_reason,
      type,
      high_value,
      secret_code,
      campaignId,
      params
    } = action.payload;

    const payload = {
      marketplace_id_hash,
      status,
      secret_code
    };

    // Only add fields that are present
    if (collaboration_type) {
      payload.collaboration_type = collaboration_type;
    }

    if (reject_reason) {
      payload.rejected_reason = reject_reason;
    }

    if (type) {
      payload.type = type;
    }

    if (high_value !== undefined) {
      payload.high_value = high_value;
    }

    const response = yield call(request, "/api/marketplace_approval", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify(payload)
    });

    if (!response?.success) {
      throw new Error(response?.message || "Failed to update request status");
    }

    showToast(response?.message || "Request status updated", "success");

    yield put(
      marketplaceActions.updateMarketplaceRequestStatusSuccess(response)
    );

    if (params) {
      yield put(
        marketplaceActions.fetchMarketplaceApprovalDetails({
          params
        })
      );
    }

    yield put(
      marketplaceActions.getMarketplaceById({
        id: campaignId
      })
    );
  } catch (error) {
    yield put(
      marketplaceActions.updateMarketplaceRequestStatusFailure(error.message)
    );
  }
}

function* fetchMarketplaceApprovalDetailsSaga(action) {
  try {
    const { params } = action.payload;

    const response = yield call(
      request,
      `/api/marketplace-details?params=${encodeURIComponent(params)}`,
      {
        method: "GET",
        credentials: "include"
      }
    );

    if (response?.success) {
      yield put(
        marketplaceActions.fetchMarketplaceApprovalDetailsSuccess(response.data)
      );
    }
  } catch (error) {
    yield put(
      marketplaceActions.fetchMarketplaceApprovalDetailsFailure(error.message)
    );
  }
}

export function* marketplaceSaga() {
  yield takeLatest(
    marketplaceActions.fetchMarketplace.type,
    fetchMarketplaceList
  );
  yield takeLatest(
    marketplaceActions.updateMarketplace.type,
    updateMarketplaceById
  );
  yield takeLatest(
    marketplaceActions.getMarketplaceById.type,
    getMarketplaceByIdSaga
  );
  yield takeLatest(
    marketplaceActions.fetchMarketplaceUser.type,
    fetchMarketplaceUserSaga
  );
  yield takeLatest(
    marketplaceActions.createMarketplaceCampaign.type,
    createMarketplaceCampaignSaga
  );
  yield takeLatest(
    marketplaceActions.fetchCampaignReports.type,
    fetchCampaignReportsSaga
  );
  yield takeLatest(
    marketplaceActions.fetchPosterCounts.type,
    fetchPosterCountsSaga
  );
  yield takeLatest(
    marketplaceActions.updateCampaignReportStatus.type,
    updateCampaignReportStatusSaga
  );
  yield takeLatest(
    marketplaceActions.updateMarketplaceRequestStatus.type,
    updateMarketplaceRequestStatusSaga
  );
  yield takeLatest(
    marketplaceActions.fetchMarketplaceApprovalDetails.type,
    fetchMarketplaceApprovalDetailsSaga
  );
}
